<template>
  <div class="import">
    <div v-if="status === 'loading'" class="state">Loading extension…</div>
    <div v-else-if="status === 'error'" class="state error">
      Failed to load extension.
      <div class="error-details">{{ errorMessage }}</div>
    </div>
    <div v-else class="import-content">
      <div v-if="activeUrl" class="hero">
        <img
          class="hero-image"
          :src="activeUrl"
          :alt="
            entryTitle ? `${entryTitle} screenshot` : 'Extension screenshot'
          "
          loading="lazy"
          @click="openLightbox"
        />
      </div>
      <div v-if="screenshotUrls.length > 1" class="thumbs">
        <img
          v-for="(url, idx) in screenshotUrls.slice(0, 5)"
          :key="url"
          class="thumb"
          :class="{ 'thumb--active': url === activeUrl }"
          :src="url"
          :alt="
            entryTitle
              ? `${entryTitle} screenshot ${idx + 1}`
              : `Extension screenshot ${idx + 1}`
          "
          loading="lazy"
          @click="setActive(url)"
        />
      </div>

      <div class="meta">
        <div class="kv" v-if="categoryLabels.length > 0">
          <div class="k">Categories</div>
          <div class="v">{{ categoryLabels.join(", ") }}</div>
        </div>
        <div class="kv" v-if="creatorLabel">
          <div class="k">Creator</div>
          <div class="v">{{ creatorLabel }}</div>
        </div>
        <div class="kv">
          <div class="k">Available</div>
          <div class="v">{{ availableLabel }}</div>
        </div>
        <div class="kv">
          <div class="k">Certified</div>
          <div class="v">{{ certifiedLabel }}</div>
        </div>
        <div class="kv" v-if="contentSummary">
          <div class="k">Contents</div>
          <div class="v">{{ contentSummary }}</div>
        </div>
      </div>

      <div class="description" v-if="descriptionText">
        <div class="k">Description</div>
        <p class="v">{{ descriptionText }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue";
import { useModal } from "vue-final-modal";

import ImageLightboxModal from "@/components/modals/ImageLightboxModal.vue";

export default defineComponent({
  name: "ImportOverview",
  props: {
    status: {
      type: String as PropType<"loading" | "ready" | "error">,
      required: true,
    },
    errorMessage: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    subtitle: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    entryTitle: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    primaryScreenshotUrl: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    screenshotUrls: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => [],
    },
    categoryLabels: { type: Array as PropType<string[]>, required: true },
    creatorLabel: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    availableLabel: { type: String, required: true },
    certifiedLabel: { type: String, required: true },
    descriptionText: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    contentSummary: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
  },
  setup(props) {
    const activeUrl = ref<string | null>(props.primaryScreenshotUrl);

    watch(
      () => props.primaryScreenshotUrl,
      (url) => {
        activeUrl.value = url;
      }
    );

    const setActive = (url: string) => {
      activeUrl.value = url;
    };

    const openLightbox = () => {
      const src = activeUrl.value;
      if (!src) return;
      const { open } = useModal({
        component: ImageLightboxModal,
        attrs: { src },
      });
      open();
    };

    return { activeUrl, openLightbox, setActive };
  },
});
</script>

<style scoped lang="scss">
.import {
  .state.error {
    .error-details {
      margin-top: 8px;
      white-space: pre-wrap;
      word-break: break-word;
      @include themed() {
        color: t($grey-60);
      }
    }
  }

  .import-content {
    display: flex;
    flex-direction: column;
    row-gap: 12px;
  }

  .hero {
    border-radius: $border-radius-m;
    overflow: hidden;
    @include themed() {
      border: 1px solid t($grey-20);
      background: t($grey-05);
    }
  }

  .hero-image {
    display: block;
    width: 100%;
    height: min(240px, 30vh);
    object-fit: cover;
    cursor: zoom-in;
  }

  .thumbs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .thumb {
    width: 96px;
    height: 64px;
    object-fit: cover;
    border-radius: $border-radius-s;
    @include transition(opacity 0.1s ease-out);
    @include themed() {
      border: 1px solid t($grey-20);
    }

    &:hover {
      opacity: 0.85;
    }

    &.thumb--active {
      @include themed() {
        border-color: t($grey-60);
      }
    }
  }

  .meta {
    margin-top: 4px;
  }

  .kv {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 12px;
    padding: 8px 0;
    @include themed() {
      border-bottom: 1px solid t($grey-10);
    }

    .k {
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }
    }

    .v {
      @extend %font-caption-regular;
      min-width: 0;
      word-break: break-word;
      @include themed() {
        color: t($grey-80);
      }
    }
  }

  .description {
    margin-top: 8px;

    .k {
      @extend %font-caption-medium;
      @include themed() {
        color: t($grey-60);
      }
    }

    .v {
      margin: 6px 0 0 0;
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-80);
      }
    }
  }
}
</style>
